import { Injectable, inject } from '@angular/core';

import { NotebookVariablesService } from '../notebook-variables/notebook-variables.service';

import { MessageService } from '../message/message.service';

import {
  ChunkContext,
  ExecutionContext,
} from '../../interfaces/chunk/chunk-context.interface';
import { FileService } from '../file/file.service';
import { JsRunner, JavascriptRunnerService } from './javascript-runner.service';
import { JavaScriptHelper } from './javascript.helper';

// Services for custom methods
import { RenderService } from '../../services/render/render.service';
import { FatClientService } from '../../services/fat-client/fat-client.service';
import { CanvasService } from '../canvas/canvas.service';
import { ArrowWebsocketService } from '../arrow-websocket/arrow-websocket.service';
import { ObjectStorageFileService } from '../object-storage/object-storage-file.service';
import { CacheService } from '../cache/cache.service';
import { LocalforageService } from '../localforage/localforage.service';
import { FileUnifiedService } from '../file-unified/file-unified.service';
import { DuckDbService } from '../duck-db/duck-db.service';
import { ConvertService } from '../convert/convert.service';
import { SimpleUIService } from '../simple-ui/simple-ui.service';
import { AppStateService } from '../app-state/app-state.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { ServiceCallBridgeService } from '../service-call/service-call-bridge.service';
import { PushDestinationService } from '../push-destination/push-destination.service';
import { SecretsService } from '../secrets/secrets.service';
import { IntrospectionBridgeService } from '../python/bridges/introspection-bridge.service';
import { StepService } from '../step/step.service';
import { StepTypeService } from '../step-type/step-type.service';
import { ConnectionService } from '../connection/connection.service';
import { DeploymentInfoService } from '../deployment-info/deployment-info.service';

import {
  CUSTOM_METHODS_EXPORT,
  CUSTOM_METHODS_IMPORT,
  EHQ,
  EHQ_UPPER,
  BOX,
  BOX_UPPER,
  ARROW_FLIGHT_COMPAT,
  ARROW_FLIGHT_SHORT,
  ARROW_FLIGHT,
} from '../../constants/additional-methods.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { HeavyServicesReadinessHelper } from '../../helpers/heavy-services-readiness.helper';
import { ProjectVariablesService } from '../project-variables/project-variables.service';
import { Sam2WebsocketService } from '../sam2-websocket/sam2-websocket.service';
import { NotebookDefaultFilesService } from '../notebook-default-file/notebook-default-file.service';
import { NotebookFilesService } from '../notebook-files/notebook-files.service';
import { ProjectStateService } from '../project-state/project-state.service';
import { RoutineService } from '../routine/routine.service';
import { BoxService } from '../box/box.service';
import { WasmModuleService } from '../wasm/wasm-module/wasm-module.service';
import { createWasmJsBindings } from '../wasm/wasm-module/wasm-bindings.helper';

@Injectable({
  providedIn: 'root',
})
export class JavascriptService {
  private readonly localforageService = inject(LocalforageService);
  private readonly fatClientService = inject(FatClientService);
  private readonly renderService = inject(RenderService);
  private readonly fileUnifiedService = inject(FileUnifiedService);
  private readonly canvasService = inject(CanvasService);
  private readonly arrowWebsocketService = inject(ArrowWebsocketService);
  private readonly objectStorageFileService = inject(ObjectStorageFileService);
  private readonly cacheService = inject(CacheService);
  private readonly duckDbService = inject(DuckDbService);
  private readonly simpleUIService = inject(SimpleUIService);
  private readonly convertService = inject(ConvertService);
  private readonly sam2WebsocketService = inject(Sam2WebsocketService);
  private readonly wasmModuleService = inject(WasmModuleService);
  private readonly notebookDefaultFilesService = inject(NotebookDefaultFilesService);
  private readonly notebookFilesService = inject(NotebookFilesService);
  private readonly notebookVariablesService = inject(NotebookVariablesService);
  private readonly messageService = inject(MessageService);
  private readonly fileService = inject(FileService);
  private readonly jsRunnerService = inject(JavascriptRunnerService);
  private readonly projectVariablesService = inject(ProjectVariablesService);
  private readonly stateService = inject(ProjectStateService);
  private readonly routineService = inject(RoutineService);
  private readonly boxService = inject(BoxService);
  private readonly appStateService = inject(AppStateService);
  private readonly oauthService = inject(OAuthService);
  private readonly pushDestinationService = inject(PushDestinationService);
  private readonly secretsService = inject(SecretsService);
  private readonly stepService = inject(StepService);
  private readonly stepTypeService = inject(StepTypeService);
  private readonly connectionService = inject(ConnectionService);
  private readonly deploymentInfoService = inject(DeploymentInfoService);

  // Service call service for EHQ.call() from JS steps.
  // Wraps ServiceCallBridgeService with the signature expected by CUSTOM_METHODS_IMPORT.
  // See: ehq-wilkins/dev-docs/guides/global-service-protocol.md
  private readonly serviceCallBridge = new ServiceCallBridgeService(this.oauthService, this.appStateService);
  private readonly serviceCallService = {
    call: async (args: unknown[]): Promise<unknown> => {
      const connectionName = args[0] as string;
      const method = args[1] as string;
      const callArgs = args[2] ?? {};
      return this.serviceCallBridge.call(connectionName, method, callArgs as Record<string, unknown>);
    },
  };

  // Full introspection bridge for JS steps (SCRUM-596). Mirrors PythonService's
  // IntrospectionBridgeService instance so EHQ.listConnections and related APIs
  // resolve through CUSTOM_METHODS_IMPORT instead of undefined.
  private introspectionBridge?: IntrospectionBridgeService;

  private get introspectionBridgeService(): IntrospectionBridgeService {
    if (!this.introspectionBridge) {
      this.introspectionBridge = new IntrospectionBridgeService(
        this.stepService,
        this.stepTypeService,
        this.connectionService,
        this.appStateService,
        this.oauthService,
        this.secretsService,
        this.pushDestinationService,
        this.deploymentInfoService,
      );
    }
    return this.introspectionBridge;
  }

  private isLoaded: boolean = false;
  private messageShown: boolean = false;
  private initializationPromise?: Promise<void>;

  /**
   * Initialize the JavaScript service. This should be called from app.config.ts
   * @returns Promise that resolves when initialization is complete
   */
  public async init() {
    // Prevent multiple initialization
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInit();

    try {
      await this.initializationPromise;
      this.isLoaded = true;
    } catch (error) {
      this.initializationPromise = undefined;
      this.isLoaded = false;
      console.error('Failed to initialize JavaScript service:', error);
      throw error;
    }
  }

  private async performInit() {
    await this.jsRunnerService.ensureInitialized();
  }

  private async startRuntime() {
    // This method is kept for compatibility but delegates to init()
    await this.init();
  }

  private async ensureInitialized() {
    if (!this.isLoaded) {
      try {
        await this.init();
      } catch (error) {
        console.error('Failed to initialize JavaScript service on demand:', error);
        throw new Error('JavaScript service failed to initialize. Please refresh the page.');
      }
    }
  }

  /**
   * Create a runner for executing JavaScript code in debug mode
   * Note: This method maintains synchronous interface for backward compatibility
   * but throws an error as async runner creation is required
   * @param chunkContext The chunk context
   * @returns JsRunner instance
   * @deprecated Use run() method instead which properly handles async runner creation
   */
  public createRunner(_chunkContext: ChunkContext): JsRunner {
    if (!this.isLoaded) {
      throw new Error('JavaScript service is not initialized. Runtime is still loading.');
    }

    // Note: createRunner is now async in JavascriptRunnerService, but we need to maintain sync interface
    // This is a breaking change that requires refactoring
    throw new Error('Synchronous runner creation is no longer supported. Use the run() method instead.');
  }

  /**
   * Dispose a JavaScript runner and free its resources
   * @param runner The runner to dispose
   */
  public disposeRunner(runner: JsRunner) {
    this.jsRunnerService.disposeRunner(runner);
  }

  /**
   * Execute a script using an existing runner
   * @param runner The runner to use for execution
   * @param script The JavaScript code to execute
   * @param context The execution context for output and messages
   * @returns Promise that resolves with the execution result
   */
  public async runScript(
    runner: JsRunner,
    script: string,
    context: ExecutionContext
  ) {
    if (!this.isLoaded) {
      if (!this.messageShown) {
        context.addMessage('JS runtime still loading', 'info');
        this.messageShown = true;
      }
      throw new Error('JS runtime is still loading');
    }

    try {
      JavaScriptHelper.addGlobalData(
        runner.context,
        runner.chunk,
        this.notebookVariablesService,
        this.projectVariablesService
      );

      const result = await this.jsRunnerService.runScript(runner, script);

      return result;
    } catch (error: any) {
      if (error.quickJSError) {
        context.addOutput(
          runner.context.dump(error.quickJSError),
          'error',
          'javascript'
        );
      }

      context.addMessage('Script execution failed: ' + error.message, 'danger');

      throw error;
    }
  }

  /**
   * Main method to execute JavaScript code in a chunk context
   * This method handles the complete lifecycle: initialization, execution, and cleanup
   * @param chunkContext The chunk context containing the code to execute
   * @returns Promise that resolves with the updated chunk or undefined
   */
  public async run(chunkContext: ChunkContext) {
    // Ensure initialization before execution
    await this.ensureInitialized();

    // ✅ Wait for heavy services and context readiness
    await HeavyServicesReadinessHelper.waitForReadiness();

    chunkContext.clearMessages();

    let content = this.getContentWithVariablesReplaced(
      chunkContext.getChunkContent()
    );
    const chunkData = chunkContext.getChunk();

    if (typeof content !== 'string' || !content) {
      return;
    }

    // Soft check: if not loaded after waiting, attempt initialization
    if (!this.isLoaded) {
      console.warn('JavascriptService still not loaded after waiting, attempting initialization...');
      chunkContext.addMessage('Initializing JavaScript runtime...', 'info');
      await this.init();
    }

    let runner: JsRunner | undefined;

    try {
      // Create runner using async method
      runner = await this.jsRunnerService.createRunner(chunkData);

      // Setup context
      JavaScriptHelper.addGlobalData(
        runner.context,
        chunkData,
        this.notebookVariablesService,
        this.projectVariablesService
      );
      JavaScriptHelper.addConsoleLog(runner.context, chunkContext);
      this.processMethodsWithGlobalNames(runner.context, chunkContext);
      this.setupWasmJsBindings(runner.context);
      JavaScriptHelper.addFetchToContext(runner.context);
      JavaScriptHelper.addPrintToContext(runner.context, chunkContext);
      JavaScriptHelper.addGetJobArgumentsToContext(runner.context, this.notebookVariablesService);

      // Process global modules
      content = await JavaScriptHelper.processGlobalModules(
        content,
        this.fileService
      );

      if (!runner.context.alive) {
        throw new Error('Context is not alive');
      }

      // Strip `await` keywords — Asyncify makes host async functions synchronous,
      // so `await` is unnecessary at runtime. The linter validates `await` syntax separately.
      const codeForExecution = content.replace(/\bawait\b\s*/g, '');
      const code = await runner.context.evalCodeAsync(codeForExecution);

      if (code.error) {
        chunkContext.addOutput(JavaScriptHelper.dumpBinaryAware(runner.context, code.error), 'error', 'javascript');
        code.error.dispose();
      } else {
        const dumpedValue = JavaScriptHelper.dumpBinaryAware(runner.context, code.value);
        if (dumpedValue !== undefined && dumpedValue !== null) {
          chunkContext.addOutput(dumpedValue, 'success', 'javascript', true);
        } else {
          const hasStreamedOutput = chunkContext.output().length > 0;
          if (!hasStreamedOutput) {
            chunkContext.addOutput('Code executed successfully', 'success', 'javascript');
          }
        }
        runner.context.unwrapResult(code).dispose();
      }

      // Extract variables — ALWAYS, even if code had error
      // (user may have set data.* before the error line)
      try {
        if (runner.context.alive) {
          this.notebookVariablesService.addVarsFromRawToList(
            runner.context.getProp(runner.context.global, 'data').consume(runner.context.dump),
            chunkData
          );
        }
      } catch (varError: any) {
        console.warn('Failed to extract variables:', varError?.message);
      }

      return chunkContext.getChunk();

    } catch (error: any) {
      // Handle "Lifetime not alive" errors gracefully
      if (error.message?.includes('Lifetime not alive') || error.message?.includes('Context was disposed')) {
        console.warn(`QuickJS context lifecycle error for step ${chunkData?.stepId}: ${error.message}`);
        return chunkContext.getChunk();
      }
      throw new Error(error.message || 'Unknown error occurred');
    } finally {
      if (runner) {
        this.disposeRunner(runner);
      }
      chunkContext.setBusy(false, 'done');
    }
  }

  /**
   * Prepare a runner for debugging. disposeRunner must be called after debugging is done
   * @param chunkContext The chunk/execution context to run the code in
   * @returns Promise that resolves with the JsRunner to debug the code
   */
  public async startDebugger(chunkContext: ChunkContext): Promise<JsRunner> {
    if (!this.isLoaded) {
      throw new Error('JavaScript service is not initialized. Cannot start debugger.');
    }

    // Use async createRunner from JavascriptRunnerService
    const chunk = chunkContext.getChunk();
    const runner = await this.jsRunnerService.createRunner(chunk);

    JavaScriptHelper.addGlobalData(
      runner.context,
      chunk,
      this.notebookVariablesService,
      this.projectVariablesService
    );
    JavaScriptHelper.addConsoleLog(runner.context, chunkContext);
    this.processMethodsWithGlobalNames(runner.context, chunkContext);
    this.setupWasmJsBindings(runner.context);
    JavaScriptHelper.addFetchToContext(runner.context);
    JavaScriptHelper.addPrintToContext(runner.context, chunkContext);
    JavaScriptHelper.addGetJobArgumentsToContext(runner.context, this.notebookVariablesService);

    return runner;
  }

  /**
   * Execute JavaScript code in the debugger
   * @param runner The runner that startDebugger returned
   * @param content The JavaScript code to execute
   * @param execContext The chunk/execution context to run the code in
   * @param prepContent Should the content be prepped (handle file services, replace variables) before execution
   */
  public async executeInDebugger(runner: JsRunner, content: string, execContext: ExecutionContext, prepContent: boolean = false) {
    if (prepContent) {
      content = this.getContentWithVariablesReplaced(content);
      content = await JavaScriptHelper.processGlobalModules(
        content,
        this.fileService
      );
    }
    try {
      // Strip `await` keywords — Asyncify makes host async functions synchronous,
      // so `await` is unnecessary at runtime. The linter validates `await` syntax separately.
      const codeForExecution = content.replace(/\bawait\b\s*/g, '');
      const code = await runner.context.evalCodeAsync(codeForExecution);
      if (code.error) {
        execContext.addOutput(JavaScriptHelper.dumpBinaryAware(runner.context, code.error), 'error', 'javascript');
        code.error.dispose();
      } else {
        const dumpedValue = JavaScriptHelper.dumpBinaryAware(runner.context, code.value);
        if (dumpedValue !== undefined && dumpedValue !== null) {
          execContext.addOutput(dumpedValue, 'success', 'javascript');
        }
        runner.context.unwrapResult(code).dispose();
        this.notebookVariablesService.addVarsFromRawToList(
          runner.context.getProp(runner.context.global, 'data').consume(runner.context.dump),
          runner.chunk
        );
      }
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  /**
   * Set up WASM module bindings in the QuickJS context.
   * Creates a `modules` global object with sub-objects for each loaded module.
   */
  private setupWasmJsBindings(jsContext: any): void {
    const wasmBindings = createWasmJsBindings(this.wasmModuleService);
    if (!wasmBindings) return;

    // Create the `modules` global object in QuickJS
    const modulesHandle = jsContext.newObject();

    for (const [moduleName, funcs] of Object.entries(wasmBindings)) {
      const moduleHandle = jsContext.newObject();

      for (const [funcName, fn] of Object.entries(funcs as Record<string, Function>)) {
        const funcHandle = jsContext.newFunction(funcName, (argsHandle: any) => {
          try {
            const args = jsContext.dump(argsHandle);
            const result = fn(args || {});
            return jsContext.newString(JSON.stringify(result));
          } catch (e: any) {
            return jsContext.newError(e.message);
          }
        });
        jsContext.setProp(moduleHandle, funcName, funcHandle);
        funcHandle.dispose();
      }

      jsContext.setProp(modulesHandle, moduleName, moduleHandle);
      moduleHandle.dispose();
    }

    jsContext.setProp(jsContext.global, 'modules', modulesHandle);
    modulesHandle.dispose();
  }

  private processMethodsWithGlobalNames(
    jsContext: any,
    chunkContext: ChunkContext
  ): any {
    const allMethods = CUSTOM_METHODS_EXPORT.concat(CUSTOM_METHODS_IMPORT);
    const globalNames = GeneralHelpers.getUniqueGlobalNames(allMethods);

    if (Array.isArray(globalNames) && globalNames.length > 0) {
      for (const element of globalNames) {
        const globalName = element;
        const exportMethods = CUSTOM_METHODS_EXPORT.filter(
          (method: any) => method.globalName === globalName
        );
        const importMethods = CUSTOM_METHODS_IMPORT.filter(
          (method: any) => method.globalName === globalName
        );
        this.processCustomMethods(
          jsContext,
          importMethods,
          exportMethods,
          globalName,
          chunkContext
        );
      }
    }

    // Register uppercase/compat aliases so both cases work
    // (e.g. BOX.downloadFile and box.downloadFile both resolve)
    const aliases: [string, string][] = [
      [EHQ, EHQ_UPPER],
      [BOX, BOX_UPPER],
      [ARROW_FLIGHT, ARROW_FLIGHT_COMPAT],
      [ARROW_FLIGHT, ARROW_FLIGHT_SHORT],
    ];
    for (const [source, alias] of aliases) {
      const handle = jsContext.getProp(jsContext.global, source);
      if (handle) {
        jsContext.setProp(jsContext.global, alias, handle);
        handle.dispose();
      }
    }

    return jsContext;
  }

  private processCustomMethods(
    context: any,
    importMethods: any[],
    exportMethods: any[],
    globalName: string,
    chunkContext: ChunkContext
  ) {
    const mainHandler = context.newObject();

    // Export methods
    // Methods will be called from JS
    for (const element of exportMethods) {
      if (element.isAsync) {
        // Async export methods: use newAsyncifiedFunction so `await` works
        const methodHandle = context.newAsyncifiedFunction(
          element.method,
          async (...args: any) => {
            const argsProcessed = args.map((a: any) => JavaScriptHelper.dumpBinaryAware(context, a));
            const calledServiceContext = (this as any)[element.serviceToUse];

            const data = await calledServiceContext[element.methodToCall](
              argsProcessed,
              chunkContext,
              'javascript'
            );

            if (data == null) {
              return JavaScriptHelper.createContextEntity(context, true);
            }

            return JavaScriptHelper.createContextEntity(context, data);
          }
        );
        methodHandle.consume((fn: any) =>
          context.setProp(mainHandler, element.method, fn)
        );
      } else {
        // Sync export methods: fire-and-forget
        const methodHandler = context.newFunction(
          element.method,
          (...args: any) => {
            const calledServiceContext = (this as any)[element.serviceToUse];

            calledServiceContext[element.methodToCall](
              args.map((a: any) => JavaScriptHelper.dumpBinaryAware(context, a)),
              chunkContext,
              'javascript'
            );
          }
        );

        context.setProp(mainHandler, element.method, methodHandler);

        methodHandler.dispose();
      }
    }
    // end

    // Import methods
    for (const element of importMethods) {
      const methodHandle = context.newAsyncifiedFunction(
        element.method,
        async (...args: any) => {
          const argsProcessed: any[] = [];

          if (args?.length > 0) {
            args.forEach((arg: any) => argsProcessed.push(JavaScriptHelper.dumpBinaryAware(context, arg)));
          }

          const calledServiceContext = (this as any)[element.serviceToUse];
          const data = await calledServiceContext[element.methodToCall](
            argsProcessed,
            chunkContext,
            'javascript'
          );

          return JavaScriptHelper.toQuickJsValue(context, data);
        }
      );
      methodHandle.consume((fn: any) =>
        context.setProp(mainHandler, element.method, fn)
      );
    }
    // end

    context.setProp(context.global, globalName, mainHandler);

    mainHandler.dispose();

    return context;
  }

  private getContentWithVariablesReplaced(content: string): string {
    const chunkContent = GeneralHelpers.replaceGlobals(
      content,
      this.projectVariablesService.variableList
    );
    return chunkContent;
  }

  private async preloadModule(moduleName: string): Promise<void> {
    const module = await this.localforageService.getItem(moduleName);
    if (!module) {
      const scriptContent = await fetch(`assets/js/${moduleName}.min.js`).then(
        (response) => response.text()
      );
      await this.localforageService.setItem(moduleName, scriptContent);
    }
  }

  private async addScriptToContent(scriptName: string, content: string): Promise<string> {
    const scriptContent = await this.localforageService.getItem(scriptName);
    if (scriptContent) {
      return `${scriptContent}
            ${content}`;
    }

    return content;
  }
}
